import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, LogIn, LogOut } from "lucide-react";

export default function ClockAdmin() {
  const { sid } = useParams();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [records, setRecords] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState(sid || "all");
  const [selectedStore, setSelectedStore] = useState(sid || user?.store_id || "");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    // When entering /admin/stores/:sid/time-clock, force filter to that store.
    if (sid) { setStoreFilter(sid); setSelectedStore(sid); }
  }, [sid]);

  const loadAll = () => {
    api.get("/clock/status").then((r) => setStatus(r.data));
    const effective = sid || (storeFilter !== "all" ? storeFilter : "");
    const url = effective ? `/clock/records?store_id=${effective}` : "/clock/records";
    api.get(url).then((r) => setRecords(r.data));
  };

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)); }, []);
  useEffect(() => { loadAll(); }, [storeFilter]); // eslint-disable-line

  const doIn = async () => {
    try {
      await api.post("/clock/in", { store_id: selectedStore || null, notes });
      setNotes(""); loadAll(); toast.success("Clocked in");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doOut = async () => {
    try {
      const { data } = await api.post("/clock/out", { notes });
      setNotes(""); loadAll(); toast.success(`Clocked out · ${data.hours}h`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const canViewAll = ["admin","super_admin"].includes(user?.role) || (user?.role === "manager" && user?.can_view_employees);

  const totals = useMemo(() => {
    const hoursByEmp = {};
    for (const r of records) { hoursByEmp[r.employee_name] = (hoursByEmp[r.employee_name] || 0) + (r.hours || 0); }
    return hoursByEmp;
  }, [records]);

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Timekeeping</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="clock-title">Clock In / Out</h1>
        </div>
        <div className="flex items-center gap-3">
          {status?.clocked_in ? (
            <span className="inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/> On the clock since {new Date(status.record.clock_in).toLocaleTimeString()}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 text-sm text-neutral-500 bg-neutral-100 rounded-full px-4 py-1.5">
              <span className="w-2 h-2 rounded-full bg-neutral-400"/> Off the clock
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 border border-neutral-200/70 lg:col-span-1">
          <h2 className="font-display font-semibold text-lg mb-4">Punch</h2>
          {!status?.clocked_in && (
            <div className="mb-3">
              <label className="text-xs uppercase tracking-wider text-neutral-500">Store</label>
              <Select value={selectedStore || ""} onValueChange={setSelectedStore}>
                <SelectTrigger data-testid="clock-store-select"><SelectValue placeholder="Choose store"/></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="mb-3">
            <label className="text-xs uppercase tracking-wider text-neutral-500">Notes (optional)</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="clock-notes"/>
          </div>
          {status?.clocked_in ? (
            <Button onClick={doOut} className="w-full h-11 rounded-xl bg-neutral-900 hover:bg-neutral-800" data-testid="clock-out-btn"><LogOut className="w-4 h-4 mr-2"/>Clock Out</Button>
          ) : (
            <Button onClick={doIn} className="w-full h-11 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="clock-in-btn"><LogIn className="w-4 h-4 mr-2"/>Clock In</Button>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200/70 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-[hsl(var(--primary))]"/>
            <h2 className="font-display font-semibold text-lg">Hours by employee</h2>
          </div>
          {Object.keys(totals).length === 0 ? (
            <p className="text-sm text-neutral-500">No records yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(totals).map(([name, hrs]) => (
                <div key={name} className="flex justify-between p-3 rounded-lg bg-neutral-50">
                  <span className="text-sm">{name}</span>
                  <span className="font-display font-bold text-neutral-900">{hrs.toFixed(2)}h</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {canViewAll && (
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm text-neutral-500">Filter store:</label>
          <div className="w-56">
            <Select value={storeFilter} onValueChange={setStoreFilter}>
              <SelectTrigger data-testid="clock-store-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="p-4">Employee</th><th className="p-4">Store</th><th className="p-4">Clock In</th><th className="p-4">Clock Out</th><th className="p-4">Hours</th><th className="p-4">Pay</th><th className="p-4">Notes</th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {records.map((r) => (
              <tr key={r.id} data-testid={`clock-row-${r.id}`}>
                <td className="p-4 font-medium">{r.employee_name}</td>
                <td className="p-4 text-neutral-600">{r.store_name || "—"}</td>
                <td className="p-4">{new Date(r.clock_in).toLocaleString()}</td>
                <td className="p-4">{r.clock_out ? new Date(r.clock_out).toLocaleString() : <span className="text-emerald-600 text-xs font-medium">Open</span>}</td>
                <td className="p-4 font-mono">{r.hours ? `${r.hours.toFixed(2)}h` : "—"}</td>
                <td className="p-4 font-mono">{r.hours && r.hourly_rate ? `$${(r.hours * r.hourly_rate).toFixed(2)}` : "—"}</td>
                <td className="p-4 text-neutral-500 text-xs">{r.notes || "—"}</td>
              </tr>
            ))}
            {records.length === 0 && (<tr><td colSpan={7} className="p-10 text-center text-neutral-500">No records.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
